/**
 * What the card SAYS while a summary is being generated, and what it offers when
 * there is none yet.
 *
 * Pure and separate from the component because it is the only per-entity branch
 * in the card, and getting it wrong tells the user we are reading the wrong
 * thing: a deal briefing reads the opportunity and its linked account, a company
 * briefing reads the roster and pipeline, and everything else is a plain
 * timeline over recent activity.
 */

/** Which briefing this card is producing. */
export type BriefingKind = "deal" | "company" | "generic";

/**
 * The deal/company copy is scoped to the TIMELINE summary. Any other
 * `summaryType` for the same entity is a different product and must not claim to
 * be reading the opportunity.
 */
export function briefingKindFor(
  entityType: string | undefined,
  summaryType: string
): BriefingKind {
  if (summaryType !== "timeline") return "generic";
  if (entityType === "deal") return "deal";
  if (entityType === "company") return "company";
  return "generic";
}

const FIRST_STEP: Readonly<Record<BriefingKind, string>> = {
  deal: "Gathering opportunity and linked account signals...",
  company: "Synthesizing account signals across contacts and pipeline...",
  generic: "Analyzing client data...",
};

const EARLY_STEP: Readonly<Record<BriefingKind, string>> = {
  deal: "Loading deal context and buyer touchpoints...",
  company: "Connecting roster, touchpoints, and open deals...",
  generic: "Processing recent activities...",
};

export const EMPTY_STATE_PROMPT: Readonly<Record<BriefingKind, string>> = {
  deal: "No deal briefing yet. Click below to generate a summary grounded in this opportunity and activity across contacts at the company.",
  company:
    "No company briefing yet. Click below to generate an account-level summary (contacts, communications, and open pipeline).",
  generic: "No summary generated yet. Click below to analyze recent activity.",
};

/** The message shown the moment generation starts, before any elapsed time. */
export function initialStatusMessage(kind: BriefingKind): string {
  return FIRST_STEP[kind];
}

/**
 * The progress ladder, by seconds elapsed.
 *
 * Returns `null` below the first rung so a caller can leave the current message
 * in place rather than blanking it — the ladder only ever moves forward.
 */
export function statusMessageForElapsed(
  kind: BriefingKind,
  elapsedSeconds: number
): string | null {
  if (elapsedSeconds >= 10) return "Almost done, processing final insights...";
  if (elapsedSeconds >= 5) return "Generating insights...";
  if (elapsedSeconds >= 3) return "AI is analyzing patterns and themes...";
  if (elapsedSeconds >= 1) return EARLY_STEP[kind];
  return null;
}
